/**
 * CostManagementTab - Inventory & Cost Control Dashboard
 * Tracks ingredients, recipes, suppliers, COGS, and Prime Cost
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Package, DollarSign, ChefHat, Truck, AlertTriangle, TrendingUp,
  Plus, Search, RefreshCw, Edit2, Trash2, Check, X, FileText,
  BarChart3, PieChart, ArrowUp, ArrowDown, Settings, Save, Users,
  ShoppingCart, Calendar, Filter, ChevronDown, ChevronRight, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Cost status helper
const getCostStatus = (actual, target) => {
  if (actual <= target) return { label: 'On Target', color: 'text-green-600 dark:text-green-400', bg: 'bg-green-100 dark:bg-green-900/30' };
  if (actual <= target * 1.1) return { label: 'Near Target', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-900/30' };
  return { label: 'Over Target', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-900/30' };
};

// Category icons
const CATEGORY_ICONS = {
  coffee: '☕',
  dairy: '🥛',
  produce: '🥬',
  proteins: '🥩',
  dry_goods: '🌾',
  beverages: '🥤',
  packaging: '📦',
  frozen: '❄️'
};

export default function CostManagementTab() {
  // State
  const [activeSubTab, setActiveSubTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [costSummary, setCostSummary] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [canSeeCosts, setCanSeeCosts] = useState(false);  // Role-based cost visibility
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [reportPeriod, setReportPeriod] = useState('week');
  
  // Modals
  const [ingredientModalOpen, setIngredientModalOpen] = useState(false);
  const [recipeModalOpen, setRecipeModalOpen] = useState(false);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [purchaseOrderModalOpen, setPurchaseOrderModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [stockAdjustModalOpen, setStockAdjustModalOpen] = useState(false);
  
  // Form state
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  // Fetch functions defined before useEffect
  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.get('/admin/cost-management/dashboard');
      setDashboard(res.data);
      setCanSeeCosts(res.data.can_see_costs || false);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/admin/cost-management/categories');
      setCategories(res.data.categories || []);
      setUnits(res.data.units || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, []);

  const fetchIngredients = useCallback(async () => {
    try {
      const res = await api.get('/admin/cost-management/ingredients');
      setIngredients(res.data.ingredients || []);
    } catch (err) {
      console.error('Failed to fetch ingredients:', err);
    }
  }, []);

  const fetchRecipes = useCallback(async () => {
    try {
      const res = await api.get('/admin/cost-management/recipes');
      setRecipes(res.data.recipes || []);
    } catch (err) {
      console.error('Failed to fetch recipes:', err);
    }
  }, []);

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await api.get('/admin/cost-management/suppliers');
      setSuppliers(res.data.suppliers || []);
    } catch (err) {
      console.error('Failed to fetch suppliers:', err);
    }
  }, []);

  const fetchLowStock = useCallback(async () => {
    try {
      const res = await api.get('/admin/cost-management/reports/low-stock');
      setLowStock(res.data.low_stock_items || []);
    } catch (err) {
      console.error('Failed to fetch low stock:', err);
    }
  }, []);

  const fetchMenuItems = useCallback(async () => {
    try {
      const res = await api.get('/menu');
      setMenuItems(res.data || []);
    } catch (err) {
      console.error('Failed to fetch menu items:', err);
    }
  }, []);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchDashboard(),
      fetchCategories(),
      fetchIngredients(),
      fetchRecipes(),
      fetchSuppliers(),
      fetchLowStock(),
      fetchMenuItems()
    ]);
    setLoading(false);
  }, [fetchDashboard, fetchCategories, fetchIngredients, fetchRecipes, fetchSuppliers, fetchLowStock, fetchMenuItems]);

  // Fetch data on mount
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Save ingredient
  const saveIngredient = async () => {
    setSaving(true);
    try {
      const url = editingItem 
        ? `/admin/cost-management/ingredients/${editingItem.id}`
        : '/admin/cost-management/ingredients';
      
      if (editingItem) {
        await api.put(url, formData);
      } else {
        await api.post(url, formData);
      }

      toast.success(editingItem ? 'Ingredient updated' : 'Ingredient created');
      setIngredientModalOpen(false);
      setEditingItem(null);
      setFormData({});
      fetchIngredients();
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save ingredient');
    }
    setSaving(false);
  };

  // Save recipe
  const saveRecipe = async () => {
    setSaving(true);
    try {
      const url = editingItem 
        ? `/admin/cost-management/recipes/${editingItem.id}`
        : '/admin/cost-management/recipes';
      
      if (editingItem) {
        await api.put(url, formData);
      } else {
        await api.post(url, formData);
      }

      toast.success(editingItem ? 'Recipe updated' : 'Recipe created');
      setRecipeModalOpen(false);
      setEditingItem(null);
      setFormData({});
      fetchRecipes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save recipe');
    }
    setSaving(false);
  };

  // Save supplier
  const saveSupplier = async () => {
    setSaving(true);
    try {
      const url = editingItem 
        ? `/admin/cost-management/suppliers/${editingItem.id}`
        : '/admin/cost-management/suppliers';
      
      if (editingItem) {
        await api.put(url, formData);
      } else {
        await api.post(url, formData);
      }

      toast.success(editingItem ? 'Supplier updated' : 'Supplier created');
      setSupplierModalOpen(false);
      setEditingItem(null);
      setFormData({});
      fetchSuppliers();
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save supplier');
    }
    setSaving(false);
  };

  // Create purchase order
  const createPurchaseOrder = async () => {
    setSaving(true);
    try {
      await api.post('/admin/cost-management/purchase-orders', formData);
      toast.success('Purchase order created & stock updated');
      setPurchaseOrderModalOpen(false);
      setFormData({});
      fetchIngredients();
      fetchLowStock();
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create purchase order');
    }
    setSaving(false);
  };

  // Adjust stock
  const adjustStock = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/admin/cost-management/ingredients/${editingItem.id}/adjust-stock`, {
        ingredient_id: editingItem.id,
        adjustment_qty: parseInt(formData.adjustment_qty) || 0,
        reason: formData.reason || 'manual_adjustment'
      });

      toast.success(`Stock adjusted: ${res.data.previous_servings} → ${res.data.new_servings}`);
      setStockAdjustModalOpen(false);
      setEditingItem(null);
      setFormData({});
      fetchIngredients();
      fetchLowStock();
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to adjust stock');
    }
    setSaving(false);
  };

  // Delete handlers
  const deleteIngredient = async (id) => {
    if (!confirm('Delete this ingredient?')) return;
    try {
      await api.delete(`/admin/cost-management/ingredients/${id}`);
      toast.success('Ingredient deleted');
      fetchIngredients();
      fetchDashboard();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const deleteRecipe = async (id) => {
    if (!confirm('Delete this recipe?')) return;
    try {
      await api.delete(`/admin/cost-management/recipes/${id}`);
      toast.success('Recipe deleted');
      fetchRecipes();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const deleteSupplier = async (id) => {
    if (!confirm('Delete this supplier?')) return;
    try {
      await api.delete(`/admin/cost-management/suppliers/${id}`);
      toast.success('Supplier deleted');
      fetchSuppliers();
      fetchDashboard();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Filter helpers
  const filteredIngredients = ingredients.filter(ing => {
    if (searchQuery && !ing.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (categoryFilter !== 'all' && ing.category !== categoryFilter) return false;
    return true;
  });

  const filteredRecipes = recipes.filter(r => {
    if (searchQuery && !r.menu_item_name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (categoryFilter !== 'all' && r.category !== categoryFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="cost-management-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cost-management-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading text-foreground">
            {canSeeCosts ? 'Cost Control' : 'Inventory Management'}
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            {canSeeCosts 
              ? 'Track ingredients, recipes, suppliers & profitability'
              : 'Track ingredients, recipes & suppliers'
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAllData}
            className="rounded-full"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {canSeeCosts && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSettingsModalOpen(true)}
              className="rounded-full"
            >
              <Settings className="w-4 h-4 mr-2" />
              Targets
            </Button>
          )}
        </div>
      </div>

      {/* Sub-tabs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
        <TabsList className={`grid w-full ${canSeeCosts ? 'grid-cols-5' : 'grid-cols-4'} lg:w-auto lg:inline-flex rounded-full bg-muted p-1`}>
          <TabsTrigger value="dashboard" className="rounded-full data-[state=active]:bg-card">
            <BarChart3 className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="ingredients" className="rounded-full data-[state=active]:bg-card">
            <Package className="w-4 h-4 mr-2" />
            Ingredients
          </TabsTrigger>
          <TabsTrigger value="recipes" className="rounded-full data-[state=active]:bg-card">
            <ChefHat className="w-4 h-4 mr-2" />
            Recipes
          </TabsTrigger>
          <TabsTrigger value="suppliers" className="rounded-full data-[state=active]:bg-card">
            <Truck className="w-4 h-4 mr-2" />
            Suppliers
          </TabsTrigger>
          {canSeeCosts && (
            <TabsTrigger value="reports" className="rounded-full data-[state=active]:bg-card">
              <PieChart className="w-4 h-4 mr-2" />
              Reports
            </TabsTrigger>
          )}
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="mt-6">
          <DashboardView 
            dashboard={dashboard} 
            lowStock={lowStock}
            canSeeCosts={canSeeCosts}
            onOpenPurchaseOrder={() => setPurchaseOrderModalOpen(true)}
            onRefresh={fetchAllData}
          />
        </TabsContent>

        {/* Ingredients Tab */}
        <TabsContent value="ingredients" className="mt-6">
          <IngredientsView
            ingredients={filteredIngredients}
            categories={categories}
            suppliers={suppliers}
            canSeeCosts={canSeeCosts}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            onAdd={() => {
              setEditingItem(null);
              setFormData({ category: 'coffee', unit_of_purchase: 'bag', low_stock_threshold: 10 });
              setIngredientModalOpen(true);
            }}
            onEdit={(ing) => {
              setEditingItem(ing);
              setFormData(ing);
              setIngredientModalOpen(true);
            }}
            onDelete={deleteIngredient}
            onAdjustStock={(ing) => {
              setEditingItem(ing);
              setFormData({ adjustment_qty: 0, reason: 'manual_adjustment' });
              setStockAdjustModalOpen(true);
            }}
          />
        </TabsContent>

        {/* Recipes Tab */}
        <TabsContent value="recipes" className="mt-6">
          <RecipesView
            recipes={filteredRecipes}
            menuItems={menuItems}
            ingredients={ingredients}
            canSeeCosts={canSeeCosts}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            onAdd={() => {
              setEditingItem(null);
              setFormData({ category: 'food', ingredients: [] });
              setRecipeModalOpen(true);
            }}
            onEdit={(recipe) => {
              setEditingItem(recipe);
              setFormData(recipe);
              setRecipeModalOpen(true);
            }}
            onDelete={deleteRecipe}
          />
        </TabsContent>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers" className="mt-6">
          <SuppliersView
            suppliers={suppliers}
            categories={categories}
            canSeeCosts={canSeeCosts}
            onAdd={() => {
              setEditingItem(null);
              setFormData({ items_supplied: [], payment_terms: 'COD' });
              setSupplierModalOpen(true);
            }}
            onEdit={(sup) => {
              setEditingItem(sup);
              setFormData(sup);
              setSupplierModalOpen(true);
            }}
            onDelete={deleteSupplier}
            onCreatePO={(sup) => {
              setFormData({ 
                supplier_id: sup.id, 
                items: [],
                order_date: new Date().toISOString().split('T')[0]
              });
              setPurchaseOrderModalOpen(true);
            }}
          />
        </TabsContent>

        {/* Reports Tab - Only shown if canSeeCosts */}
        {canSeeCosts && (
          <TabsContent value="reports" className="mt-6">
            <ReportsView
              reportPeriod={reportPeriod}
              setReportPeriod={setReportPeriod}
            />
          </TabsContent>
        )}
      </Tabs>

      {/* Ingredient Modal */}
      <Dialog open={ingredientModalOpen} onOpenChange={setIngredientModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Ingredient' : 'Add Ingredient'}</DialogTitle>
            <DialogDescription>
              Track cost and servings for inventory management
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Coffee Beans (House Blend)"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={formData.category || 'coffee'}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unit of Purchase</Label>
                <Select
                  value={formData.unit_of_purchase || 'bag'}
                  onValueChange={(v) => setFormData({ ...formData, unit_of_purchase: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {units.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Purchase Cost ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.purchase_cost || ''}
                  onChange={(e) => setFormData({ ...formData, purchase_cost: parseFloat(e.target.value) || 0 })}
                  placeholder="45.00"
                />
              </div>
              <div>
                <Label>Servings per Unit</Label>
                <Input
                  type="number"
                  value={formData.servings_per_unit || ''}
                  onChange={(e) => setFormData({ ...formData, servings_per_unit: parseInt(e.target.value) || 1 })}
                  placeholder="65"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Low Stock Threshold</Label>
                <Input
                  type="number"
                  value={formData.low_stock_threshold || 10}
                  onChange={(e) => setFormData({ ...formData, low_stock_threshold: parseInt(e.target.value) || 10 })}
                  placeholder="10"
                />
              </div>
              <div>
                <Label>Supplier (Optional)</Label>
                <Select
                  value={formData.supplier_id || 'none'}
                  onValueChange={(v) => setFormData({ ...formData, supplier_id: v === 'none' ? null : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Supplier</SelectItem>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {formData.purchase_cost > 0 && formData.servings_per_unit > 0 && (
              <div className="bg-muted/50 rounded-xl p-3 text-sm">
                <p className="text-muted-foreground">
                  Cost per serving: <span className="font-bold text-foreground">
                    ${(formData.purchase_cost / formData.servings_per_unit).toFixed(4)}
                  </span>
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIngredientModalOpen(false)}>Cancel</Button>
            <Button onClick={saveIngredient} disabled={saving || !formData.name}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recipe Modal */}
      <Dialog open={recipeModalOpen} onOpenChange={setRecipeModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Recipe' : 'Create Recipe'}</DialogTitle>
            <DialogDescription>
              Link a menu item to its ingredients for cost tracking
            </DialogDescription>
          </DialogHeader>
          <RecipeForm
            formData={formData}
            setFormData={setFormData}
            menuItems={menuItems}
            ingredients={ingredients}
            recipes={recipes}
            editingItem={editingItem}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecipeModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={saveRecipe} 
              disabled={saving || !formData.menu_item_id || (formData.ingredients || []).length === 0}
            >
              {saving ? 'Saving...' : 'Save Recipe'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplier Modal */}
      <Dialog open={supplierModalOpen} onOpenChange={setSupplierModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Supplier' : 'Add Supplier'}</DialogTitle>
            <DialogDescription>
              Track supplier contact info and order history
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Company Name *</Label>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Fiji Coffee Traders"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Person</Label>
                <Input
                  value={formData.contact_name || ''}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+679 XXX XXXX"
                />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="supplier@example.com"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={formData.address || ''}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main St, Nadi, Fiji"
              />
            </div>
            <div>
              <Label>Payment Terms</Label>
              <Select
                value={formData.payment_terms || 'COD'}
                onValueChange={(v) => setFormData({ ...formData, payment_terms: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="COD">Cash on Delivery</SelectItem>
                  <SelectItem value="Net 7">Net 7 Days</SelectItem>
                  <SelectItem value="Net 14">Net 14 Days</SelectItem>
                  <SelectItem value="Net 30">Net 30 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSupplierModalOpen(false)}>Cancel</Button>
            <Button onClick={saveSupplier} disabled={saving || !formData.name}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purchase Order Modal */}
      <Dialog open={purchaseOrderModalOpen} onOpenChange={setPurchaseOrderModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Purchase Order</DialogTitle>
            <DialogDescription>
              Record incoming inventory and update stock levels
            </DialogDescription>
          </DialogHeader>
          <PurchaseOrderForm
            formData={formData}
            setFormData={setFormData}
            suppliers={suppliers}
            ingredients={ingredients}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurchaseOrderModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={createPurchaseOrder} 
              disabled={saving || !formData.supplier_id || (formData.items || []).length === 0}
            >
              {saving ? 'Creating...' : 'Create & Update Stock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock Adjustment Modal */}
      <Dialog open={stockAdjustModalOpen} onOpenChange={setStockAdjustModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adjust Stock: {editingItem?.name}</DialogTitle>
            <DialogDescription>
              Current servings: {editingItem?.current_servings || 0}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Adjustment (+ to add, - to deduct)</Label>
              <Input
                type="number"
                value={formData.adjustment_qty || ''}
                onChange={(e) => setFormData({ ...formData, adjustment_qty: e.target.value })}
                placeholder="+10 or -5"
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Select
                value={formData.reason || 'manual_adjustment'}
                onValueChange={(v) => setFormData({ ...formData, reason: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual_adjustment">Manual Adjustment</SelectItem>
                  <SelectItem value="waste">Waste / Spoilage</SelectItem>
                  <SelectItem value="theft">Theft / Loss</SelectItem>
                  <SelectItem value="count_correction">Count Correction</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStockAdjustModalOpen(false)}>Cancel</Button>
            <Button onClick={adjustStock} disabled={saving || !formData.adjustment_qty}>
              {saving ? 'Adjusting...' : 'Adjust Stock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={settingsModalOpen} onOpenChange={setSettingsModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cost Targets</DialogTitle>
            <DialogDescription>
              Set target percentages for cost control
            </DialogDescription>
          </DialogHeader>
          <CostTargetsForm
            initialTargets={dashboard?.targets}
            onSaved={() => {
              setSettingsModalOpen(false);
              fetchDashboard();
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============== SUB-COMPONENTS ==============

// Dashboard View
function DashboardView({ dashboard, lowStock, canSeeCosts, onOpenPurchaseOrder, onRefresh }) {
  if (!dashboard) return null;

  const { stats, cost_summary, targets } = dashboard;

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className={`grid grid-cols-2 ${canSeeCosts ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-4`}>
        <StatCard
          icon={Package}
          label="Ingredients"
          value={stats?.total_ingredients || 0}
          color="text-blue-600"
          bgColor="bg-blue-100 dark:bg-blue-900/30"
        />
        <StatCard
          icon={ChefHat}
          label="Recipes"
          value={stats?.total_recipes || 0}
          color="text-green-600"
          bgColor="bg-green-100 dark:bg-green-900/30"
        />
        <StatCard
          icon={Truck}
          label="Suppliers"
          value={stats?.total_suppliers || 0}
          color="text-purple-600"
          bgColor="bg-purple-100 dark:bg-purple-900/30"
        />
        <StatCard
          icon={AlertTriangle}
          label="Low Stock"
          value={stats?.low_stock_items || 0}
          color={stats?.low_stock_items > 0 ? 'text-red-600' : 'text-green-600'}
          bgColor={stats?.low_stock_items > 0 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-green-100 dark:bg-green-900/30'}
        />
        {canSeeCosts && (
          <StatCard
            icon={ShoppingCart}
            label="Week Purchases"
            value={`$${(stats?.week_purchases || 0).toFixed(0)}`}
            color="text-amber-600"
            bgColor="bg-amber-100 dark:bg-amber-900/30"
          />
        )}
      </div>

      {/* Cost Percentages - Only for Owner/Accountant */}
      {canSeeCosts && (
        <div className="grid md:grid-cols-4 gap-4">
          <CostCard
            label="Food Cost"
            actual={cost_summary?.food_cost_pct || 0}
            target={targets?.food_cost_target || 30}
            icon="🍔"
          />
          <CostCard
            label="Beverage Cost"
            actual={cost_summary?.beverage_cost_pct || 0}
            target={targets?.beverage_cost_target || 20}
            icon="☕"
          />
          <CostCard
            label="Labor Cost"
            actual={cost_summary?.labor_cost_pct || 0}
            target={targets?.labor_cost_target || 30}
            icon="👤"
          />
          <CostCard
            label="Prime Cost"
            actual={cost_summary?.prime_cost_pct || 0}
            target={targets?.prime_cost_target || 60}
            icon="💰"
            highlight
          />
        </div>
      )}

      {/* Low Stock Alert */}
      {lowStock.length > 0 && (
        <div className="bg-card rounded-2xl border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h3 className="font-heading text-lg text-foreground">Low Stock Alert</h3>
            </div>
            <Button size="sm" onClick={onOpenPurchaseOrder} className="rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              Purchase Order
            </Button>
          </div>
          <div className="space-y-2">
            {lowStock.slice(0, 5).map(item => (
              <div key={item.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                <div>
                  <p className="font-medium text-foreground">{item.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {CATEGORY_ICONS[item.category]} {item.category}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${item.current_servings <= 0 ? 'text-red-600' : 'text-amber-600'}`}>
                    {item.current_servings} servings
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Order {item.suggested_order_qty} {item.unit_of_purchase}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Stat Card
function StatCard({ icon: Icon, label, value, color, bgColor }) {
  return (
    <div className="bg-card rounded-2xl p-4 border border-border">
      <div className={`w-10 h-10 ${bgColor} rounded-xl flex items-center justify-center mb-3`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

// Cost Card
function CostCard({ label, actual, target, icon, highlight }) {
  const status = getCostStatus(actual, target);
  
  return (
    <div className={`bg-card rounded-2xl p-4 border ${highlight ? 'border-primary' : 'border-border'}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl">{icon}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full ${status.bg} ${status.color}`}>
          {status.label}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <div className="flex items-baseline gap-2 mt-1">
        <span className={`text-2xl font-bold ${status.color}`}>{actual.toFixed(1)}%</span>
        <span className="text-sm text-muted-foreground">/ {target}%</span>
      </div>
      <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full ${actual <= target ? 'bg-green-500' : actual <= target * 1.1 ? 'bg-amber-500' : 'bg-red-500'}`}
          style={{ width: `${Math.min((actual / target) * 100, 100)}%` }}
        />
      </div>
    </div>
  );
}

// Ingredients View
function IngredientsView({ 
  ingredients, categories, suppliers, canSeeCosts, searchQuery, setSearchQuery,
  categoryFilter, setCategoryFilter, onAdd, onEdit, onDelete, onAdjustStock 
}) {
  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search ingredients..."
            className="pl-10 rounded-full"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-40 rounded-full">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={onAdd} className="rounded-full">
          <Plus className="w-4 h-4 mr-2" />
          Add Ingredient
        </Button>
      </div>

      {/* Table */}
      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Ingredient</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Category</th>
                {canSeeCosts && (
                  <>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Cost/Unit</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Cost/Serving</th>
                  </>
                )}
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Stock</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {ingredients.length === 0 ? (
                <tr>
                  <td colSpan={canSeeCosts ? 6 : 4} className="py-12 text-center text-muted-foreground">
                    No ingredients found. Add your first ingredient to start tracking inventory.
                  </td>
                </tr>
              ) : (
                ingredients.map(ing => {
                  const isLow = ing.current_servings <= ing.low_stock_threshold;
                  const isOut = ing.current_servings <= 0;
                  
                  return (
                    <tr key={ing.id} className="border-b border-border hover:bg-muted/30">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-foreground">{ing.name}</p>
                          {ing.supplier_name && (
                            <p className="text-xs text-muted-foreground">{ing.supplier_name}</p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm">
                          {CATEGORY_ICONS[ing.category]} {ing.category}
                        </span>
                      </td>
                      {canSeeCosts && (
                        <>
                          <td className="py-3 px-4 text-right font-mono">
                            ${ing.purchase_cost?.toFixed(2)} / {ing.unit_of_purchase}
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-sm">
                            ${ing.cost_per_serving?.toFixed(4)}
                          </td>
                        </>
                      )}
                      <td className="py-3 px-4 text-right">
                        <span className={`font-bold ${isOut ? 'text-red-600' : isLow ? 'text-amber-600' : 'text-foreground'}`}>
                          {ing.current_servings || 0}
                        </span>
                        <span className="text-muted-foreground text-sm"> / {ing.low_stock_threshold}</span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onAdjustStock(ing)}
                            className="h-8 px-2"
                          >
                            <ArrowUp className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onEdit(ing)}
                            className="h-8 px-2"
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onDelete(ing.id)}
                            className="h-8 px-2 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Recipes View
function RecipesView({ 
  recipes, menuItems, ingredients, canSeeCosts, searchQuery, setSearchQuery,
  categoryFilter, setCategoryFilter, onAdd, onEdit, onDelete 
}) {
  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search recipes..."
            className="pl-10 rounded-full"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-40 rounded-full">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="food">Food</SelectItem>
            <SelectItem value="beverage">Beverage</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={onAdd} className="rounded-full">
          <Plus className="w-4 h-4 mr-2" />
          Add Recipe
        </Button>
      </div>

      {/* Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {recipes.length === 0 ? (
          <div className="col-span-full py-12 text-center text-muted-foreground">
            No recipes found. Link menu items to ingredients to track costs.
          </div>
        ) : (
          recipes.map(recipe => {
            const costStatus = recipe.cost_percentage <= (recipe.category === 'food' ? 30 : 20) 
              ? 'good' 
              : recipe.cost_percentage <= (recipe.category === 'food' ? 35 : 25) 
                ? 'warning' 
                : 'bad';
            
            return (
              <div key={recipe.id} className="bg-card rounded-2xl border border-border p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-medium text-foreground">{recipe.menu_item_name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{recipe.category}</p>
                  </div>
                  {canSeeCosts && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      costStatus === 'good' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                      costStatus === 'warning' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                      'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                      {recipe.cost_percentage?.toFixed(1)}% cost
                    </span>
                  )}
                </div>
                
                {canSeeCosts ? (
                  <div className="space-y-1 text-sm mb-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Menu Price:</span>
                      <span className="font-mono">${recipe.menu_price?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">COGS:</span>
                      <span className="font-mono">${recipe.total_cost?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span>Margin:</span>
                      <span className="text-green-600">
                        ${((recipe.menu_price || 0) - (recipe.total_cost || 0)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1 text-sm mb-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Menu Price:</span>
                      <span className="font-mono">${recipe.menu_price?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                )}
                
                <div className="text-xs text-muted-foreground mb-3">
                  {recipe.ingredients?.length || 0} ingredients
                </div>
                
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => onEdit(recipe)} className="flex-1">
                    <Edit2 className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => onDelete(recipe.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// Suppliers View
function SuppliersView({ suppliers, categories, canSeeCosts, onAdd, onEdit, onDelete, onCreatePO }) {
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-end">
        <Button onClick={onAdd} className="rounded-full">
          <Plus className="w-4 h-4 mr-2" />
          Add Supplier
        </Button>
      </div>

      {/* Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {suppliers.length === 0 ? (
          <div className="col-span-full py-12 text-center text-muted-foreground">
            No suppliers found. Add your first supplier to track orders.
          </div>
        ) : (
          suppliers.map(sup => (
            <div key={sup.id} className="bg-card rounded-2xl border border-border p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-medium text-foreground">{sup.name}</p>
                  {sup.contact_name && (
                    <p className="text-xs text-muted-foreground">{sup.contact_name}</p>
                  )}
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                  {sup.payment_terms || 'COD'}
                </span>
              </div>
              
              <div className="space-y-1 text-sm mb-3">
                {sup.phone && (
                  <p className="text-muted-foreground">{sup.phone}</p>
                )}
                {sup.email && (
                  <p className="text-muted-foreground text-xs">{sup.email}</p>
                )}
              </div>
              
              <div className={`flex ${canSeeCosts ? 'justify-between' : 'justify-start'} text-sm mb-3 p-2 bg-muted/50 rounded-lg`}>
                <div>
                  <p className="text-muted-foreground text-xs">Orders</p>
                  <p className="font-bold">{sup.total_orders || 0}</p>
                </div>
                {canSeeCosts && (
                  <div className="text-right">
                    <p className="text-muted-foreground text-xs">Total Spent</p>
                    <p className="font-bold">${(sup.total_spent || 0).toFixed(0)}</p>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => onCreatePO(sup)} className="flex-1">
                  <ShoppingCart className="w-3 h-3 mr-1" />
                  Order
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onEdit(sup)}>
                  <Edit2 className="w-3 h-3" />
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => onDelete(sup.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Reports View
function ReportsView({ reportPeriod, setReportPeriod }) {
  const [profitability, setProfitability] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const [profRes, sumRes] = await Promise.all([
        api.get('/admin/cost-management/reports/profitability', { params: { period: reportPeriod } }),
        api.get('/admin/cost-management/reports/summary', { params: { period: reportPeriod } })
      ]);

      setProfitability(profRes.data);
      setSummary(sumRes.data);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
    }
    setLoading(false);
  }, [reportPeriod]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center gap-4">
        <Select value={reportPeriod} onValueChange={setReportPeriod}>
          <SelectTrigger className="w-40 rounded-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Today</SelectItem>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchReports} className="rounded-full">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-card rounded-2xl p-4 border border-border">
            <p className="text-sm text-muted-foreground">Revenue</p>
            <p className="text-2xl font-bold text-foreground">${summary.revenue?.total?.toFixed(0) || 0}</p>
            <p className="text-xs text-muted-foreground">{summary.revenue?.order_count || 0} orders</p>
          </div>
          <div className="bg-card rounded-2xl p-4 border border-border">
            <p className="text-sm text-muted-foreground">Gross Profit</p>
            <p className="text-2xl font-bold text-green-600">${summary.gross_profit?.amount?.toFixed(0) || 0}</p>
            <p className="text-xs text-green-600">{summary.gross_profit?.percentage?.toFixed(1) || 0}% margin</p>
          </div>
          <div className="bg-card rounded-2xl p-4 border border-border">
            <p className="text-sm text-muted-foreground">Total COGS</p>
            <p className="text-2xl font-bold text-foreground">
              ${((summary.food_cost?.amount || 0) + (summary.beverage_cost?.amount || 0)).toFixed(0)}
            </p>
            <p className="text-xs text-muted-foreground">Food + Beverage</p>
          </div>
          <div className="bg-card rounded-2xl p-4 border border-border">
            <p className="text-sm text-muted-foreground">Prime Cost</p>
            <p className={`text-2xl font-bold ${summary.prime_cost?.status === 'good' ? 'text-green-600' : 'text-red-600'}`}>
              {summary.prime_cost?.percentage?.toFixed(1) || 0}%
            </p>
            <p className="text-xs text-muted-foreground">Target: {summary.targets?.prime_cost_target || 60}%</p>
          </div>
        </div>
      )}

      {/* Profitability Table */}
      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="font-heading text-lg text-foreground">Profitability by Item</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Item</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Sold</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Revenue</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">COGS</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Profit</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Margin</th>
              </tr>
            </thead>
            <tbody>
              {!profitability?.items?.length ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-muted-foreground">
                    {loading ? 'Loading...' : 'No data for this period. Create recipes to track profitability.'}
                  </td>
                </tr>
              ) : (
                profitability.items.slice(0, 20).map((item, i) => (
                  <tr key={i} className="border-b border-border hover:bg-muted/30">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-foreground">{item.menu_item_name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{item.category}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">{item.quantity_sold}</td>
                    <td className="py-3 px-4 text-right font-mono">${item.revenue?.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right font-mono">${item.total_cogs?.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right font-mono text-green-600">${item.total_profit?.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        item.margin_percentage >= 70 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        item.margin_percentage >= 60 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                        'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {item.margin_percentage?.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {profitability?.totals && (
          <div className="p-4 bg-muted/50 border-t border-border">
            <div className="flex justify-between text-sm">
              <span className="font-medium">Period Totals:</span>
              <span>
                Revenue: <span className="font-mono">${profitability.totals.revenue?.toFixed(2)}</span> | 
                COGS: <span className="font-mono">${profitability.totals.cogs?.toFixed(2)}</span> | 
                Profit: <span className="font-mono text-green-600">${profitability.totals.profit?.toFixed(2)}</span> | 
                Avg Margin: <span className="font-mono">{profitability.totals.avg_margin_pct?.toFixed(1)}%</span>
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Recipe Form Component
function RecipeForm({ formData, setFormData, menuItems, ingredients, recipes, editingItem }) {
  const [selectedIngredients, setSelectedIngredients] = useState(formData.ingredients || []);

  // Get menu items that don't have recipes yet
  const existingRecipeIds = recipes.map(r => r.menu_item_id);
  const availableMenuItems = menuItems.filter(item => 
    !existingRecipeIds.includes(item.id) || item.id === formData.menu_item_id
  );

  const addIngredient = (ingredientId) => {
    const ing = ingredients.find(i => i.id === ingredientId);
    if (!ing || selectedIngredients.some(s => s.ingredient_id === ingredientId)) return;
    
    const newIngredients = [...selectedIngredients, {
      ingredient_id: ingredientId,
      ingredient_name: ing.name,
      servings_used: 1
    }];
    setSelectedIngredients(newIngredients);
    setFormData({ ...formData, ingredients: newIngredients });
  };

  const updateServings = (ingredientId, servings) => {
    const newIngredients = selectedIngredients.map(ing => 
      ing.ingredient_id === ingredientId 
        ? { ...ing, servings_used: parseFloat(servings) || 1 }
        : ing
    );
    setSelectedIngredients(newIngredients);
    setFormData({ ...formData, ingredients: newIngredients });
  };

  const removeIngredient = (ingredientId) => {
    const newIngredients = selectedIngredients.filter(ing => ing.ingredient_id !== ingredientId);
    setSelectedIngredients(newIngredients);
    setFormData({ ...formData, ingredients: newIngredients });
  };

  const handleMenuItemChange = (itemId) => {
    const item = menuItems.find(m => m.id === itemId);
    if (item) {
      setFormData({
        ...formData,
        menu_item_id: item.id,
        menu_item_name: item.name,
        category: item.category === 'coffee' || item.category?.includes('drink') || item.category?.includes('smoothie') 
          ? 'beverage' 
          : 'food'
      });
    }
  };

  // Calculate total cost
  const totalCost = selectedIngredients.reduce((sum, sel) => {
    const ing = ingredients.find(i => i.id === sel.ingredient_id);
    return sum + (sel.servings_used * (ing?.cost_per_serving || 0));
  }, 0);

  const menuItem = menuItems.find(m => m.id === formData.menu_item_id);
  const costPercentage = menuItem?.price ? (totalCost / menuItem.price * 100) : 0;

  return (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Menu Item *</Label>
          <Select
            value={formData.menu_item_id || ''}
            onValueChange={handleMenuItemChange}
            disabled={!!editingItem}
          >
            <SelectTrigger><SelectValue placeholder="Select menu item" /></SelectTrigger>
            <SelectContent>
              {availableMenuItems.map(item => (
                <SelectItem key={item.id} value={item.id}>
                  {item.name} (${item.price})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Category</Label>
          <Select
            value={formData.category || 'food'}
            onValueChange={(v) => setFormData({ ...formData, category: v })}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="food">Food</SelectItem>
              <SelectItem value="beverage">Beverage</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Add Ingredient */}
      <div>
        <Label>Add Ingredient</Label>
        <Select onValueChange={addIngredient}>
          <SelectTrigger><SelectValue placeholder="Select ingredient to add" /></SelectTrigger>
          <SelectContent>
            {ingredients
              .filter(ing => !selectedIngredients.some(s => s.ingredient_id === ing.id))
              .map(ing => (
                <SelectItem key={ing.id} value={ing.id}>
                  {ing.name} (${ing.cost_per_serving?.toFixed(4)}/serving)
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {/* Selected Ingredients */}
      <div className="border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-muted/50 border-b">
              <th className="text-left py-2 px-3 text-sm">Ingredient</th>
              <th className="text-right py-2 px-3 text-sm w-24">Servings</th>
              <th className="text-right py-2 px-3 text-sm w-24">Cost</th>
              <th className="py-2 px-3 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {selectedIngredients.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-4 text-center text-muted-foreground text-sm">
                  No ingredients added yet
                </td>
              </tr>
            ) : (
              selectedIngredients.map(sel => {
                const ing = ingredients.find(i => i.id === sel.ingredient_id);
                const cost = sel.servings_used * (ing?.cost_per_serving || 0);
                
                return (
                  <tr key={sel.ingredient_id} className="border-b last:border-b-0">
                    <td className="py-2 px-3 text-sm">{sel.ingredient_name || ing?.name}</td>
                    <td className="py-2 px-3">
                      <Input
                        type="number"
                        step="0.5"
                        min="0.5"
                        value={sel.servings_used}
                        onChange={(e) => updateServings(sel.ingredient_id, e.target.value)}
                        className="h-8 text-right"
                      />
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-sm">
                      ${cost.toFixed(4)}
                    </td>
                    <td className="py-2 px-3">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeIngredient(sel.ingredient_id)}
                        className="h-6 w-6 p-0 text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Cost Summary */}
      {selectedIngredients.length > 0 && (
        <div className="bg-muted/50 rounded-xl p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span>Menu Price:</span>
            <span className="font-mono">${menuItem?.price?.toFixed(2) || '0.00'}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Total COGS:</span>
            <span className="font-mono">${totalCost.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm font-bold">
            <span>Margin:</span>
            <span className="text-green-600">${((menuItem?.price || 0) - totalCost).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Cost %:</span>
            <span className={costPercentage <= 30 ? 'text-green-600' : costPercentage <= 35 ? 'text-amber-600' : 'text-red-600'}>
              {costPercentage.toFixed(1)}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Purchase Order Form
function PurchaseOrderForm({ formData, setFormData, suppliers, ingredients }) {
  const [orderItems, setOrderItems] = useState(formData.items || []);

  const addItem = (ingredientId) => {
    const ing = ingredients.find(i => i.id === ingredientId);
    if (!ing || orderItems.some(o => o.ingredient_id === ingredientId)) return;
    
    const newItems = [...orderItems, {
      ingredient_id: ingredientId,
      ingredient_name: ing.name,
      quantity: 1,
      unit_cost: ing.purchase_cost || 0
    }];
    setOrderItems(newItems);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (ingredientId, field, value) => {
    const newItems = orderItems.map(item => 
      item.ingredient_id === ingredientId
        ? { ...item, [field]: parseFloat(value) || 0 }
        : item
    );
    setOrderItems(newItems);
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (ingredientId) => {
    const newItems = orderItems.filter(item => item.ingredient_id !== ingredientId);
    setOrderItems(newItems);
    setFormData({ ...formData, items: newItems });
  };

  const totalCost = orderItems.reduce((sum, item) => sum + (item.quantity * item.unit_cost), 0);

  return (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Supplier *</Label>
          <Select
            value={formData.supplier_id || ''}
            onValueChange={(v) => setFormData({ ...formData, supplier_id: v })}
          >
            <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
            <SelectContent>
              {suppliers.map(sup => (
                <SelectItem key={sup.id} value={sup.id}>{sup.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Order Date</Label>
          <Input
            type="date"
            value={formData.order_date || ''}
            onChange={(e) => setFormData({ ...formData, order_date: e.target.value })}
          />
        </div>
      </div>

      {/* Add Item */}
      <div>
        <Label>Add Item</Label>
        <Select onValueChange={addItem}>
          <SelectTrigger><SelectValue placeholder="Select item to order" /></SelectTrigger>
          <SelectContent>
            {ingredients
              .filter(ing => !orderItems.some(o => o.ingredient_id === ing.id))
              .map(ing => (
                <SelectItem key={ing.id} value={ing.id}>
                  {ing.name} (${ing.purchase_cost?.toFixed(2)}/{ing.unit_of_purchase})
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {/* Order Items */}
      <div className="border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-muted/50 border-b">
              <th className="text-left py-2 px-3 text-sm">Item</th>
              <th className="text-right py-2 px-3 text-sm w-20">Qty</th>
              <th className="text-right py-2 px-3 text-sm w-28">Unit Cost</th>
              <th className="text-right py-2 px-3 text-sm w-24">Total</th>
              <th className="py-2 px-3 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {orderItems.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-4 text-center text-muted-foreground text-sm">
                  No items added yet
                </td>
              </tr>
            ) : (
              orderItems.map(item => (
                <tr key={item.ingredient_id} className="border-b last:border-b-0">
                  <td className="py-2 px-3 text-sm">{item.ingredient_name}</td>
                  <td className="py-2 px-3">
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateItem(item.ingredient_id, 'quantity', e.target.value)}
                      className="h-8 text-right"
                    />
                  </td>
                  <td className="py-2 px-3">
                    <Input
                      type="number"
                      step="0.01"
                      value={item.unit_cost}
                      onChange={(e) => updateItem(item.ingredient_id, 'unit_cost', e.target.value)}
                      className="h-8 text-right"
                    />
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-sm">
                    ${(item.quantity * item.unit_cost).toFixed(2)}
                  </td>
                  <td className="py-2 px-3">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeItem(item.ingredient_id)}
                      className="h-6 w-6 p-0 text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Total */}
      {orderItems.length > 0 && (
        <div className="bg-muted/50 rounded-xl p-4 flex justify-between items-center">
          <span className="font-medium">Total Order:</span>
          <span className="text-xl font-bold">${totalCost.toFixed(2)}</span>
        </div>
      )}

      <div>
        <Label>Notes (Optional)</Label>
        <Input
          value={formData.notes || ''}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Any notes about this order..."
        />
      </div>
    </div>
  );
}

// Cost Targets Form
function CostTargetsForm({ initialTargets, onSaved }) {
  const [targets, setTargets] = useState({
    food_cost_target: initialTargets?.food_cost_target || 30,
    beverage_cost_target: initialTargets?.beverage_cost_target || 20,
    labor_cost_target: initialTargets?.labor_cost_target || 30,
    prime_cost_target: initialTargets?.prime_cost_target || 60
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/cost-management/settings', targets);
      toast.success('Cost targets updated');
      onSaved();
    } catch (err) {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Food Cost Target (%)</Label>
          <Input
            type="number"
            value={targets.food_cost_target}
            onChange={(e) => setTargets({ ...targets, food_cost_target: parseFloat(e.target.value) || 30 })}
          />
        </div>
        <div>
          <Label>Beverage Cost Target (%)</Label>
          <Input
            type="number"
            value={targets.beverage_cost_target}
            onChange={(e) => setTargets({ ...targets, beverage_cost_target: parseFloat(e.target.value) || 20 })}
          />
        </div>
        <div>
          <Label>Labor Cost Target (%)</Label>
          <Input
            type="number"
            value={targets.labor_cost_target}
            onChange={(e) => setTargets({ ...targets, labor_cost_target: parseFloat(e.target.value) || 30 })}
          />
        </div>
        <div>
          <Label>Prime Cost Target (%)</Label>
          <Input
            type="number"
            value={targets.prime_cost_target}
            onChange={(e) => setTargets({ ...targets, prime_cost_target: parseFloat(e.target.value) || 60 })}
          />
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-3 text-sm text-blue-700 dark:text-blue-300">
        <p><strong>Industry Standards:</strong></p>
        <p>Food Cost: 28-35% | Beverage Cost: 18-24% | Labor: 25-35% | Prime Cost: 55-65%</p>
      </div>

      <DialogFooter>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Targets'}
        </Button>
      </DialogFooter>
    </div>
  );
}
